/**
 * Contratti delle richieste.
 *
 * Gli schemi Zod stanno nel pacchetto condiviso perche client e server devono
 * concordare sulla forma dei dati: il client valida prima di inviare, il
 * server rivalida perche la validazione che conta e sempre la sua.
 *
 * Gli stessi schemi alimenteranno il registro delle funzioni dell'assistente
 * AI: da qui si ricava il JSON Schema da passare al modello, e con questi si
 * rivalida cio che il modello restituisce.
 */

import { z } from 'zod';
import { giorniTra } from './calendario.js';
import { FAMIGLIE_ATTRIBUTO } from './catalogo.js';
import type { TipoEvento } from './formato.js';

export const idSchema = z.string().uuid();

export const accessoSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

/**
 * L'identificativo dell'evento lo genera il client prima dell'invio. Con una
 * coda che ritenta, un invio andato a buon fine ma con risposta persa
 * produrrebbe altrimenti gol doppi: qui il reinvio e semplicemente inerte.
 */
export const eventoInSchema = z.object({
  id: idSchema,
  tipo: z.enum([
    'passaggio_completato',
    'passaggio_sbagliato',
    'palla_conquistata',
    'palla_persa',
    'tiro_specchio',
    'tiro_fuori',
    'gol',
    'autogol',
    'assist',
    'cartellino_giallo',
    'cartellino_rosso',
    'ingresso',
    'uscita',
    'gol_subito',
    'autogol_avversario',
  ]),
  giocatoreId: idSchema.nullable(),
  periodo: z.number().int().min(1).max(6),
  minuto: z.number().int().min(0).max(200),
  secondo: z.number().int().min(0).max(59).default(0),
  orarioReale: z.string().datetime(),
});

export type EventoIn = z.infer<typeof eventoInSchema>;

/** Invio a lotti: la coda del client puo accumulare piu eventi tra un battito e l'altro. */
export const inviaEventiSchema = z.object({
  sessioneId: idSchema.optional(),
  eventi: z.array(eventoInSchema).min(1).max(100),
});

export const formazioneSchema = z.object({
  convocati: z.array(idSchema).min(1).max(40),
  titolari: z.array(idSchema).min(1).max(11),
});

export const battitoSchema = z.object({
  sessioneId: idSchema,
  eventi: z.array(eventoInSchema).max(100).default([]),
});

export const periodoSchema = z.object({
  sessioneId: idSchema,
  periodo: z.number().int().min(1).max(6),
});

export const sospensioneSchema = z.object({
  sessioneId: idSchema,
  azione: z.enum(['apri', 'chiudi']),
  /** Conferma di riprendere in inferiorita quando dalla panchina non entra nessuno. */
  inferiorita: z.boolean().default(false),
});

/**
 * Stagione (V46). Le date sono giorni di calendario, senza ora: una stagione
 * non comincia a mezzanotte di un fuso. L'errore sull'ordine delle date sta
 * sul campo della fine, dove il form lo mostra.
 */
export const stagioneSchema = z
  .object({
    nome: z.string().trim().min(1, 'Il nome è obbligatorio.').max(40, 'Massimo 40 caratteri.'),
    dataInizio: z.string().date('Data non valida.'),
    dataFine: z.string().date('Data non valida.'),
  })
  .refine((s) => s.dataFine > s.dataInizio, {
    message: "La fine della stagione deve seguire l'inizio.",
    path: ['dataFine'],
  });

/**
 * Dati società (V45). I limiti non stanno nello schema di scrittura: li
 * mostra la lettura, li alza solo il Super Admin.
 */
export const modificaDatiSocietaSchema = z.object({
  nome: z.string().trim().min(1, 'Il nome è obbligatorio.').max(80, 'Massimo 80 caratteri.'),
  logoUrl: z
    .union([z.literal(''), z.string().trim().url('URL non valido.')])
    .nullish()
    .transform((u) => u || null),
});

/** Squadra (V47): contenitore stabile nel tempo, quindi nome senza anno. */
export const creaSquadraSchema = z.object({
  nome: z.string().trim().min(1, 'Il nome è obbligatorio.').max(60, 'Massimo 60 caratteri.'),
});

/** Rinomina e disattivazione si salvano insieme, dalla stessa riga. */
export const modificaSquadraSchema = creaSquadraSchema.extend({
  attiva: z.boolean(),
});

/**
 * Incrocio squadra per stagione (V48). Il formato di gioco resta ai valori
 * predefiniti: si regola dalle impostazioni della squadra-stagione (V43).
 */
export const creaSquadraStagioneSchema = z.object({
  squadraId: idSchema,
  stagioneId: idSchema,
  categoria: z
    .string()
    .trim()
    .max(60, 'Massimo 60 caratteri.')
    .optional()
    .transform((c) => c || undefined),
});

/**
 * Testo facoltativo: dal form arriva la stringa vuota, nel database va null.
 * Cosi "nessun ruolo" ha una sola rappresentazione.
 */
const testoFacoltativo = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Massimo ${max} caratteri.`)
    .nullish()
    .transform((t) => t || null);

/**
 * Nome di competizione come lo si confronta: spazi ripuliti, maiuscole
 * ignorate. "coppa  italia " e "Coppa Italia" sono la stessa competizione,
 * altrimenti le statistiche si spezzano in due.
 */
export const normalizzaNome = (nome: string): string => nome.trim().replace(/\s+/g, ' ');

export const stessoNome = (a: string, b: string): boolean =>
  normalizzaNome(a).toLocaleLowerCase('it') === normalizzaNome(b).toLocaleLowerCase('it');

/**
 * L'anno e della stagione, non della competizione: "Torneo 2025" l'anno
 * dopo diventa un doppione. Scatta con un anno a quattro cifre (1900-2099)
 * o con una coppia di stagione consecutiva come 2025/26, 25/26 o 25-26; solo
 * un avviso, perche "Under 15" o "Coppa 3° turno" sono nomi legittimi.
 */
export function nomeConAnno(nome: string): boolean {
  if (/(?<!\d)(19|20)\d{2}(?!\d)/.test(nome)) return true;
  for (const m of nome.matchAll(/(?<!\d)(\d{2}|\d{4})\s*[/-]\s*(\d{2}|\d{4})(?!\d)/g)) {
    if ((Number(m[1]) + 1) % 100 === Number(m[2]) % 100) return true;
  }
  return false;
}

/** Nome di competizione gia ripulito: lo stesso per V42 e per la creazione al volo di V17. */
export const nomeCompetizioneSchema = z
  .string()
  .transform(normalizzaNome)
  .pipe(z.string().min(1, 'Il nome è obbligatorio.').max(80, 'Massimo 80 caratteri.'));

/** Competizione (V42): si crea col solo nome, attiva. */
export const creaCompetizioneSchema = z.object({ nome: nomeCompetizioneSchema });

/** Rinomina e disattivazione si salvano insieme, dalla stessa riga. */
export const modificaCompetizioneSchema = creaCompetizioneSchema.extend({
  attiva: z.boolean(),
});

const interoTra = (min: number, max: number) =>
  z
    .number({ invalid_type_error: 'Numero non valido.' })
    .int('Numero non valido.')
    .min(min, `Da ${min} a ${max}.`)
    .max(max, `Da ${min} a ${max}.`);

/** Formato di gioco copiato sulla partita: gli stessi limiti dei CHECK su `partita`. */
export const formatoPartitaSchema = z.object({
  nPeriodi: interoTra(1, 6),
  durataPeriodoMin: interoTra(1, 60),
  giocatoriInCampo: interoTra(3, 11),
  maxSostituzioni: interoTra(0, 99),
  rientroConsentito: z.boolean(),
});

/**
 * Nuova partita e modifica finche e programmata (V17). La competizione e una
 * esistente oppure un nome da creare al volo: il server riusa quella con lo
 * stesso nome invece di duplicarla.
 */
export const partitaSchema = z.object({
  competizione: z.union([
    z.object({ id: idSchema }),
    z.object({
      nome: z
        .string()
        .transform(normalizzaNome)
        .pipe(
          z.string().min(1, 'Scegli o scrivi la competizione.').max(80, 'Massimo 80 caratteri.'),
        ),
    }),
  ]),
  dataOra: z.string().datetime({ offset: true, message: 'Data e ora non valide.' }),
  avversario: z
    .string()
    .transform(normalizzaNome)
    .pipe(z.string().min(1, "L'avversario è obbligatorio.").max(120, 'Massimo 120 caratteri.')),
  inCasa: z.boolean(),
  luogo: testoFacoltativo(200),
  note: testoFacoltativo(2000),
  formato: formatoPartitaSchema,
});

export type DatiPartita = z.infer<typeof partitaSchema>;

/**
 * Tipi di evento che le impostazioni della squadra accendono e spengono: i
 * pulsanti sotto il nome del giocatore nel Live Timer. Ingressi, uscite, gol
 * subito e fischi non sono pulsanti di un giocatore e restano sempre.
 */
export const TIPI_EVENTO_CONFIGURABILI = [
  'gol',
  'assist',
  'tiro_specchio',
  'tiro_fuori',
  'passaggio_completato',
  'passaggio_sbagliato',
  'palla_conquistata',
  'palla_persa',
  'autogol',
  'cartellino_giallo',
  'cartellino_rosso',
] as const satisfies readonly TipoEvento[];

export type TipoEventoConfigurabile = (typeof TIPI_EVENTO_CONFIGURABILI)[number];

/**
 * Sempre attivi: il gol fa il risultato, i cartellini le squalifiche e
 * l'inferiorita numerica dopo un'espulsione.
 */
export const TIPI_EVENTO_FISSI: readonly TipoEventoConfigurabile[] = [
  'gol',
  'cartellino_giallo',
  'cartellino_rosso',
];

/**
 * Impostazioni della squadra-stagione (V43). Il formato ha gli stessi limiti
 * di quello copiato sulla partita. I tipi tornano nell'ordine del catalogo,
 * senza doppioni, cosi che salvare due volte la stessa scelta dia lo stesso
 * valore.
 */
export const impostazioniSquadraSchema = z.object({
  categoria: testoFacoltativo(60),
  campionato: testoFacoltativo(80),
  girone: testoFacoltativo(40),
  formato: formatoPartitaSchema,
  tipiEventoAttivi: z
    .array(z.enum(TIPI_EVENTO_CONFIGURABILI))
    .max(50)
    .refine((tipi) => TIPI_EVENTO_FISSI.every((t) => tipi.includes(t)), {
      message: 'Gol, giallo e rosso restano sempre attivi.',
    })
    .transform((tipi) => TIPI_EVENTO_CONFIGURABILI.filter((t) => tipi.includes(t))),
});

export type ImpostazioniSquadra = z.infer<typeof impostazioniSquadraSchema>;

/**
 * Convocati senza titolari (V19): si salvano anche giorni prima, la partita
 * resta programmata. Un elenco vuoto toglie tutte le convocazioni.
 */
export const convocatiSchema = z.object({
  convocati: z
    .array(idSchema)
    .max(40, 'Massimo 40 convocati.')
    .transform((ids) => [...new Set(ids)]),
});

export const RUOLI_GIOCATORE = ['portiere', 'difensore', 'centrocampista', 'attaccante'] as const;
export type RuoloGiocatore = (typeof RUOLI_GIOCATORE)[number];

/** Etichette del ruolo preferito del giocatore, non dei ruoli degli account. */
export const ETICHETTA_RUOLO_GIOCATORE: Record<RuoloGiocatore, string> = {
  portiere: 'Portiere',
  difensore: 'Difensore',
  centrocampista: 'Centrocampista',
  attaccante: 'Attaccante',
};

/**
 * Stato del giocatore: come sta la persona, qualunque competizione giochi.
 * Solo l'infortunato cambia qualcosa oltre all'avviso: all'appello nasce
 * infortunato invece che presente.
 */
export const STATI_GIOCATORE = ['disponibile', 'affaticato', 'recupero', 'infortunato'] as const;
export type StatoGiocatore = (typeof STATI_GIOCATORE)[number];

export const ETICHETTA_STATO_GIOCATORE: Record<StatoGiocatore, string> = {
  disponibile: 'Disponibile',
  affaticato: 'Affaticato',
  recupero: 'Recupero',
  infortunato: 'Infortunato',
};

/**
 * Stato sportivo: vale per la coppia giocatore e competizione. Chi non ne ha
 * uno in una competizione e regolare.
 */
export const STATI_SPORTIVI = ['diffidato', 'squalificato'] as const;
export type StatoSportivo = (typeof STATI_SPORTIVI)[number];

export const ETICHETTA_STATO_SPORTIVO: Record<StatoSportivo, string> = {
  diffidato: 'Diffidato',
  squalificato: 'Squalificato',
};

/** Anagrafica del giocatore: la modifica solo l'Admin Squadra (V36). */
export const anagraficaGiocatoreSchema = z.object({
  nome: z.string().trim().min(1, 'Il nome è obbligatorio.').max(60, 'Massimo 60 caratteri.'),
  cognome: z.string().trim().min(1, 'Il cognome è obbligatorio.').max(60, 'Massimo 60 caratteri.'),
  dataNascita: z
    .union([z.literal(''), z.string().date('Data non valida.')])
    .nullish()
    .transform((d) => d || null),
});

/**
 * Ruolo e piede sono di campo, non di anagrafica: li cambia anche il Mister
 * (V36), con la regola degli stati operativi. Il ruolo e obbligatorio, il
 * piede no.
 */
export const campoGiocatoreSchema = z.object({
  piede: z
    .union([z.literal(''), z.enum(['destro', 'sinistro', 'ambidestro'])])
    .nullish()
    .transform((p) => p || null),
  ruoloPreferito: z.enum(RUOLI_GIOCATORE, { message: 'Scegli il ruolo.' }),
});

/** Il giocatore intero, come nasce (V41). */
export const giocatoreSchema = anagraficaGiocatoreSchema.merge(campoGiocatoreSchema);

const numeroMaglia = z
  .number({ invalid_type_error: 'Numero non valido.' })
  .int('Numero non valido.')
  .min(1, 'Il numero va da 1 a 99.')
  .max(99, 'Il numero va da 1 a 99.')
  .nullish()
  .transform((n) => n ?? null);

/** Il nuovo giocatore puo entrare subito in una rosa, nella stessa operazione. */
export const nuovoGiocatoreSchema = giocatoreSchema.extend({
  tesseramento: z.object({ squadraStagioneId: idSchema, numeroMaglia }).optional(),
});

/**
 * Stato del giocatore: lo cambia anche il Mister, quindi viaggia separato
 * dall'anagrafica, che per lui e in sola lettura.
 */
export const statoGiocatoreSchema = z.object({
  stato: z.enum(STATI_GIOCATORE, { message: 'Stato non valido.' }),
});

/** Stato sportivo in una competizione. Null lo toglie: torna regolare. */
export const statoSportivoSchema = z.object({
  competizioneId: idSchema,
  stato: z.enum(STATI_SPORTIVI, { message: 'Stato non valido.' }).nullable(),
});

/** Note sul giocatore: le scrive lo staff come ruolo e piede. Vuote, null. */
export const noteGiocatoreSchema = z.object({
  note: z
    .string()
    .trim()
    .max(2000, 'Massimo 2000 caratteri.')
    .nullish()
    .transform((n) => n || null),
});

/** Tesseramento aggiunto dalla rosa (V07): la data di inizio e oggi. */
export const nuovoTesseramentoSchema = z.object({
  giocatoreId: idSchema,
  numeroMaglia,
});

/** Tesseramento (V08). Chiuderlo significa dargli una data di fine. */
export const modificaTesseramentoSchema = z
  .object({
    numeroMaglia,
    dataInizio: z.string().date('Data non valida.'),
    dataFine: z
      .union([z.literal(''), z.string().date('Data non valida.')])
      .nullish()
      .transform((d) => d || null),
  })
  .refine((t) => t.dataFine === null || t.dataFine >= t.dataInizio, {
    message: "La fine non può precedere l'inizio.",
    path: ['dataFine'],
  });

/**
 * Import della rosa (V09): i giocatori scelti dalla rosa di origine entrano
 * nella destinazione con nuovi tesseramenti, da oggi.
 */
export const importRosaSchema = z.object({
  origineId: idSchema,
  giocatori: z
    .array(idSchema)
    .min(1, 'Scegli almeno un giocatore.')
    .transform((ids) => [...new Set(ids)]),
  azzeraNumeri: z.boolean().default(false),
});

/**
 * Scala 1–10 a mezzi punti dei voti. Il CHECK SQL ripete lo stesso
 * invariante: qui si ferma il client.
 */
export const valoreScalaSchema = z
  .number({ invalid_type_error: 'Valore non valido.' })
  .min(1, 'Il valore va da 1 a 10.')
  .max(10, 'Il valore va da 1 a 10.')
  .refine((v) => v * 2 === Math.trunc(v * 2), {
    message: 'Solo mezzi punti: 6, 6.5, 7…',
  });

/**
 * Scala 1–100 a interi degli attributi. Il CHECK SQL su `misurazione` ripete
 * lo stesso invariante.
 */
export const valoreAttributoSchema = z
  .number({ invalid_type_error: 'Valore non valido.' })
  .int('Solo numeri interi.')
  .min(1, 'Il valore va da 1 a 100.')
  .max(100, 'Il valore va da 1 a 100.');

/** Nuova o corretta misurazione (V38). */
export const misurazioneSchema = z.object({
  attributoId: idSchema,
  valore: valoreAttributoSchema,
  data: z.string().date('Data non valida.'),
});

/**
 * Un voto per evento (V39). Esattamente una tra partita e allenamento, come
 * il CHECK sulla tabella.
 */
export const votoGiocatoreSchema = z
  .object({
    partitaId: idSchema.optional(),
    allenamentoId: idSchema.optional(),
    valore: valoreScalaSchema,
  })
  .refine((v) => (v.partitaId != null) !== (v.allenamentoId != null), {
    message: 'Il voto è di una partita oppure di un allenamento.',
  });

const idFiltro = z
  .union([z.literal(''), idSchema])
  .optional()
  .transform((v) => v || undefined);

const dataFiltro = z
  .union([z.literal(''), z.string().date('Data non valida.')])
  .optional()
  .transform((v) => v || undefined);

/**
 * Filtri condivisi delle schede Attributi, Voti e Statistiche. Dalla query
 * string arrivano anche vuoti: spariscono, non restano come stringa.
 */
export const filtriSchedaSchema = z
  .object({
    stagione: idFiltro,
    da: dataFiltro,
    a: dataFiltro,
    competizione: idFiltro,
    squadraStagione: idFiltro,
  })
  .transform((f) => {
    const puliti: {
      stagione?: string;
      da?: string;
      a?: string;
      competizione?: string;
      squadraStagione?: string;
    } = {};
    if (f.stagione) puliti.stagione = f.stagione;
    if (f.da) puliti.da = f.da;
    if (f.a) puliti.a = f.a;
    if (f.competizione) puliti.competizione = f.competizione;
    if (f.squadraStagione) puliti.squadraStagione = f.squadraStagione;
    return puliti;
  });

export const LUOGHI_PARTITA = ['casa', 'trasferta'] as const;
export type LuogoPartita = (typeof LUOGHI_PARTITA)[number];

/**
 * Filtri delle statistiche di squadra (V32, V33): la competizione, il
 * periodo, sul giorno della partita, estremi compresi, e il luogo (in casa o
 * in trasferta).
 */
export const filtriStatisticheSchema = z
  .object({
    competizione: idFiltro,
    da: dataFiltro,
    a: dataFiltro,
    luogo: z
      .union([z.literal(''), z.enum(LUOGHI_PARTITA, { message: 'Luogo non valido.' })])
      .optional()
      .transform((v) => v || undefined),
  })
  .transform((f) => {
    const puliti: { competizione?: string; da?: string; a?: string; luogo?: LuogoPartita } = {};
    if (f.competizione) puliti.competizione = f.competizione;
    if (f.da) puliti.da = f.da;
    if (f.a) puliti.a = f.a;
    if (f.luogo) puliti.luogo = f.luogo;
    return puliti;
  });

/** Un periodo facoltativo, estremi compresi: le presenze di V34. */
export const filtriPeriodoSchema = z.object({ da: dataFiltro, a: dataFiltro }).transform((f) => {
  const puliti: { da?: string; a?: string } = {};
  if (f.da) puliti.da = f.da;
  if (f.a) puliti.a = f.a;
  return puliti;
});

const nomeAttributo = z
  .string()
  .trim()
  .min(1, 'Il nome è obbligatorio.')
  .max(40, 'Massimo 40 caratteri.');

/** Nuovo attributo del catalogo (V52). La famiglia resta quella della creazione. */
export const creaAttributoSchema = z.object({
  nome: nomeAttributo,
  famiglia: z.enum(FAMIGLIE_ATTRIBUTO, { message: 'Famiglia non valida.' }),
});

/** Rinomina e disattivazione. */
export const modificaAttributoSchema = z.object({
  nome: nomeAttributo,
  attivo: z.boolean(),
});

/**
 * Sessione di misurazioni per la rosa (V30). Una data unica, una riga per
 * cella compilata: ogni salvataggio aggiunge, non sovrascrive.
 */
export const sessioneMisurazioniSchema = z.object({
  data: z.string().date('Data non valida.'),
  rilevazioni: z
    .array(
      z.object({
        giocatoreId: idSchema,
        attributoId: idSchema,
        valore: valoreAttributoSchema,
      }),
    )
    .min(1, 'Inserisci almeno una misurazione.'),
});

const idGiocatori = z
  .array(idSchema)
  .min(1, 'Scegli almeno un giocatore.')
  .transform((ids) => [...new Set(ids)]);

/**
 * Query del confronto (V31). Dal browser arriva una stringa di id separati
 * da virgola; i test e l'assistente possono passare l'array.
 */
export const confrontoGiocatoriSchema = z.object({
  giocatori: z.union([
    z
      .string()
      .transform((s) =>
        s
          .split(',')
          .map((p) => p.trim())
          .filter(Boolean),
      )
      .pipe(idGiocatori),
    idGiocatori,
  ]),
});

/**
 * Ruolo assegnato dall'Admin Squadra (V51, V44). Solo i ruoli di squadra:
 * l'Admin Squadra e uno per societa e lo nomina il Super Admin.
 */
export const assegnaRuoloSchema = z.object({
  ruolo: z.enum(['mister', 'dirigente'], { message: 'Scegli Mister o Dirigente.' }),
  squadraStagioneId: idSchema.or(z.literal('')).refine(Boolean, 'Scegli la squadra-stagione.'),
});

const emailSchema = z
  .string()
  .trim()
  .min(1, "L'email è obbligatoria.")
  .max(254, 'Email troppo lunga.')
  .email('Email non valida.');

/**
 * Invito di un account (V50): chi entra nella societa e con che ruoli. Almeno
 * un ruolo, perche un Mister o un Dirigente senza squadra entrerebbe senza
 * vedere nulla.
 */
export const invitaAccountSchema = z.object({
  email: emailSchema,
  nome: z.string().trim().min(1, 'Il nome è obbligatorio.').max(60, 'Massimo 60 caratteri.'),
  cognome: z.string().trim().min(1, 'Il cognome è obbligatorio.').max(60, 'Massimo 60 caratteri.'),
  ruoli: z
    .array(assegnaRuoloSchema)
    .min(1, 'Assegna almeno un ruolo.')
    .max(30, 'Massimo 30 ruoli.'),
});

export type DatiInvito = z.infer<typeof invitaAccountSchema>;

/** Password scelta da chi accetta l'invito (V04) o la reimposta (V03). */
export const nuovaPasswordSchema = z
  .string()
  .min(10, 'Almeno 10 caratteri.')
  .max(200, 'Massimo 200 caratteri.');

/** Accettazione dell'invito (V04): nome e cognome arrivano precompilati e si possono correggere. */
export const accettaInvitoSchema = z.object({
  nome: invitaAccountSchema.shape.nome,
  cognome: invitaAccountSchema.shape.cognome,
  password: nuovaPasswordSchema,
});

/** Richiesta del link di reimpostazione (V02). */
export const recuperoPasswordSchema = z.object({ email: emailSchema });

/** Nuova password dal link di reimpostazione (V03). */
export const reimpostaPasswordSchema = z.object({ password: nuovaPasswordSchema });

/** Codice di un link di invito o di reimpostazione: 32 byte in base64url. */
export const codiceSchema = z.string().regex(/^[\w-]{43}$/, 'Collegamento non valido.');

/** Disattivazione e riattivazione di un account nella societa (V49). */
export const statoAccountSchema = z.object({
  attivo: z.boolean(),
});

// ---------------------------------------------------------------------------
// Allenamenti e presenze (V11–V14)
// ---------------------------------------------------------------------------

export const STATI_ALLENAMENTO = ['programmato', 'svolto', 'annullato'] as const;
export type StatoAllenamento = (typeof STATI_ALLENAMENTO)[number];

export const STATI_PRESENZA = ['presente', 'assente', 'infortunato'] as const;
export type StatoPresenza = (typeof STATI_PRESENZA)[number];

/** Nuovo allenamento e modifica (V11). */
export const allenamentoSchema = z.object({
  dataOra: z.string().datetime({ offset: true, message: 'Data e ora non valide.' }),
  luogo: testoFacoltativo(200),
  note: testoFacoltativo(2000),
});

export type DatiAllenamento = z.infer<typeof allenamentoSchema>;

/** Cambio di stato dalla scheda (V13). */
export const statoAllenamentoSchema = z.object({
  stato: z.enum(STATI_ALLENAMENTO, { message: 'Stato non valido.' }),
});

const dataSchema = z.string().date('Data non valida.');

/**
 * Regola della creazione in blocco (V12): la si manda per avere l'anteprima.
 * Il periodo sta entro un anno, cosi come la stagione.
 */
export const bloccoAllenamentiSchema = z
  .object({
    giorni: z
      .array(z.number().int().min(1).max(7))
      .min(1, 'Scegli almeno un giorno.')
      .transform((g) => [...new Set(g)].sort() as (1 | 2 | 3 | 4 | 5 | 6 | 7)[]),
    ora: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Ora non valida.'),
    da: dataSchema,
    a: dataSchema,
    luogo: testoFacoltativo(200),
    note: testoFacoltativo(2000),
  })
  .refine((b) => b.a >= b.da, { message: "La fine precede l'inizio.", path: ['a'] })
  .refine((b) => giorniTra(b.da, b.a) <= 366, {
    message: 'Il periodo non può superare un anno.',
    path: ['a'],
  });

export type BloccoAllenamenti = z.infer<typeof bloccoAllenamentiSchema>;

/**
 * Conferma del blocco: le date scelte dall'anteprima, non la regola, e per
 * ciascuna le partite viste sovrapposte. Il server ricalcola: una
 * sovrapposizione che chi conferma non ha visto ferma la conferma.
 */
export const confermaBloccoSchema = z.object({
  luogo: testoFacoltativo(200),
  note: testoFacoltativo(2000),
  date: z
    .array(
      z.object({
        dataOra: z.string().datetime({ offset: true, message: 'Data e ora non valide.' }),
        partiteViste: z.array(idSchema).max(20),
      }),
    )
    .min(1, 'Scegli almeno una data.')
    .max(200, 'Massimo 200 date.')
    .refine((d) => new Set(d.map((x) => new Date(x.dataOra).getTime())).size === d.length, {
      message: 'Una data compare due volte.',
    }),
});

export type ConfermaBlocco = z.infer<typeof confermaBloccoSchema>;

/** Una riga dell'appello (V14). Togliere la riga riporta a "non rilevato". */
export const presenzaSchema = z.object({
  stato: z.enum(STATI_PRESENZA, { message: 'Stato non valido.' }),
});

/** Il voto di un giocatore nelle viste dell'allenamento (V15) e della partita (V27). Toglierlo e una DELETE. */
export const votoSchema = z.object({ valore: valoreScalaSchema });

/** Motivo per cui una richiesta sul timer e stata rifiutata. */
export type MotivoRifiuto =
  'sessione_persa' | 'stato_non_valido' | 'minuto_non_valido' | 'vincolo_campo';
